import type { ZoneDef, ZoneLayout } from '@jianghu/game-data';

export interface MapMarker {
  x: number;
  z: number;
  kind: 'monster' | 'npc' | 'player' | 'loot' | 'quest' | 'turnin' | 'waypoint' | 'dead';
}

const PX_PER_M = 3;

/**
 * Pre-renders the zone (terrain, roads, water, buildings, trees) once into an offscreen canvas;
 * the minimap and world map then blit/rotate it and draw live markers on top.
 */
export class MapRenderer {
  readonly base: HTMLCanvasElement;
  private readonly zone: ZoneDef;

  constructor(zone: ZoneDef, layout: ZoneLayout) {
    this.zone = zone;
    const b = zone.bounds;
    const w = (b.maxX - b.minX) * PX_PER_M;
    const h = (b.maxZ - b.minZ) * PX_PER_M;
    this.base = document.createElement('canvas');
    this.base.width = w;
    this.base.height = h;
    const ctx = this.base.getContext('2d') as CanvasRenderingContext2D;
    const X = (x: number): number => (x - b.minX) * PX_PER_M;
    const Z = (z: number): number => (z - b.minZ) * PX_PER_M;
    ctx.fillStyle = '#4a5e35';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#5b6f3e';
    ctx.beginPath();
    ctx.arc(X(0), Z(-76), 40 * PX_PER_M, 0, Math.PI * 2);
    ctx.fill();
    for (const f of zone.fields) {
      ctx.fillStyle = '#6a8250';
      ctx.fillRect(X(f.minX), Z(f.minZ), (f.maxX - f.minX) * PX_PER_M, (f.maxZ - f.minZ) * PX_PER_M);
    }
    for (const t of layout.trees) {
      ctx.fillStyle = t.kind === 'maple' ? '#8a4a2e' : t.kind === 'bamboo' ? '#3c5a28' : '#2f4a2a';
      ctx.beginPath();
      ctx.arc(X(t.x), Z(t.z), (t.kind === 'bamboo' ? 1.4 : 1.6) * t.scale * PX_PER_M, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const r of zone.roads) {
      ctx.strokeStyle = r.kind === 'stone' ? '#a8a294' : '#b49a68';
      ctx.lineWidth = r.width * PX_PER_M;
      ctx.beginPath();
      r.points.forEach(([x, z], i) => (i === 0 ? ctx.moveTo(X(x), Z(z)) : ctx.lineTo(X(x), Z(z))));
      ctx.stroke();
    }
    ctx.fillStyle = '#a8a294';
    ctx.beginPath();
    ctx.arc(X(zone.plaza.x), Z(zone.plaza.z), zone.plaza.r * PX_PER_M, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3d7a86';
    for (const wa of zone.water)
      ctx.fillRect(X(wa.x - wa.hw), Z(wa.z - wa.hd), wa.hw * 2 * PX_PER_M, wa.hd * 2 * PX_PER_M);
    for (const s of zone.structures) {
      ctx.save();
      ctx.translate(X(s.x), Z(s.z));
      ctx.rotate(-s.rot);
      switch (s.kind) {
        case 'house':
        case 'hall':
          ctx.fillStyle = '#3a3a44';
          ctx.fillRect(
            (-s.w / 2 - 0.8) * PX_PER_M,
            (-s.d / 2 - 0.8) * PX_PER_M,
            (s.w + 1.6) * PX_PER_M,
            (s.d + 1.6) * PX_PER_M,
          );
          ctx.fillStyle = '#c9a35a';
          ctx.fillRect((-s.w / 2) * PX_PER_M, -0.15 * PX_PER_M, s.w * PX_PER_M, 0.3 * PX_PER_M);
          break;
        case 'pond':
          ctx.fillStyle = '#3d7a86';
          ctx.beginPath();
          ctx.arc(0, 0, (s.w / 2) * PX_PER_M, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 'fence':
        case 'palisade':
          ctx.fillStyle = s.kind === 'fence' ? '#8a6a42' : '#5a3e2a';
          ctx.fillRect(
            (-s.w / 2) * PX_PER_M,
            (-s.d / 2 - 0.2) * PX_PER_M,
            s.w * PX_PER_M,
            (s.d + 0.4) * PX_PER_M,
          );
          break;
        case 'bridge':
          ctx.fillStyle = '#a8a294';
          ctx.fillRect((-s.w / 2) * PX_PER_M, (-s.d / 2) * PX_PER_M, s.w * PX_PER_M, s.d * PX_PER_M);
          break;
        case 'tent':
          ctx.fillStyle = '#7b6a4e';
          ctx.fillRect((-s.w / 2) * PX_PER_M, (-s.d / 2) * PX_PER_M, s.w * PX_PER_M, s.d * PX_PER_M);
          break;
        case 'gate':
          ctx.fillStyle = '#9b2c2c';
          ctx.fillRect((-s.w / 2 - 0.5) * PX_PER_M, -0.5 * PX_PER_M, (s.w + 1) * PX_PER_M, 1 * PX_PER_M);
          break;
        default:
          ctx.fillStyle = '#6d6a62';
          ctx.fillRect((-s.w / 2) * PX_PER_M, (-s.d / 2) * PX_PER_M, s.w * PX_PER_M, s.d * PX_PER_M);
      }
      ctx.restore();
    }
  }

  private static markerStyle(kind: MapMarker['kind']): [string, number] {
    switch (kind) {
      case 'monster':
        return ['#e0574a', 2.6];
      case 'npc':
        return ['#ecd08e', 2.6];
      case 'player':
        return ['#8cc1f2', 3];
      case 'loot':
        return ['#ffffff', 2];
      case 'waypoint':
        return ['#6fd1a6', 3.5];
      case 'dead':
        return ['#777777', 2];
      default:
        return ['#ffd34a', 4];
    }
  }

  /** Rotating minimap centred on the player; `yaw` is the camera yaw so "up" is camera-forward. */
  drawMinimap(
    canvas: HTMLCanvasElement,
    px: number,
    pz: number,
    facing: number,
    yaw: number,
    markers: MapMarker[],
    radiusM = 45,
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    const scale = w / 2 / radiusM;
    const b = this.zone.bounds;
    ctx.save();
    ctx.clearRect(0, 0, w, h);
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#2a3322';
    ctx.fillRect(0, 0, w, h);
    ctx.translate(w / 2, h / 2);
    // Rotate so camera-forward (-sin yaw, -cos yaw) points up (-Y on the canvas).
    ctx.rotate(yaw);
    const k = scale / PX_PER_M;
    ctx.drawImage(
      this.base,
      (b.minX - px) * scale,
      (b.minZ - pz) * scale,
      this.base.width * k,
      this.base.height * k,
    );
    for (const m of markers) {
      const [color, r] = MapRenderer.markerStyle(m.kind);
      const x = (m.x - px) * scale;
      const y = (m.z - pz) * scale;
      if (x * x + y * y > (w / 2) * (w / 2)) continue;
      ctx.fillStyle = color;
      ctx.beginPath();
      if (m.kind === 'quest' || m.kind === 'turnin') {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-yaw);
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(m.kind === 'quest' ? '!' : '?', 0, 0);
        ctx.restore();
        continue;
      }
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    // Player arrow (screen space, facing relative to camera).
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-(facing - yaw) + Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#16161a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(5.5, 6);
    ctx.lineTo(0, 3);
    ctx.lineTo(-5.5, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /** Full zone map (north up) with markers. */
  drawWorldMap(
    canvas: HTMLCanvasElement,
    px: number,
    pz: number,
    facing: number,
    markers: MapMarker[],
    labels: { x: number; z: number; text: string }[],
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const b = this.zone.bounds;
    const sx = canvas.width / (b.maxX - b.minX);
    const sz = canvas.height / (b.maxZ - b.minZ);
    ctx.drawImage(this.base, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(10,10,14,0.15)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (const l of labels) {
      ctx.font = '600 13px "Noto Serif CJK SC", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillText(l.text, (l.x - b.minX) * sx + 1, (l.z - b.minZ) * sz + 1);
      ctx.fillStyle = '#f3e6c4';
      ctx.fillText(l.text, (l.x - b.minX) * sx, (l.z - b.minZ) * sz);
    }
    for (const m of markers) {
      const [color, r] = MapRenderer.markerStyle(m.kind);
      const x = (m.x - b.minX) * sx;
      const y = (m.z - b.minZ) * sz;
      ctx.fillStyle = color;
      if (m.kind === 'quest' || m.kind === 'turnin') {
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(m.kind === 'quest' ? '!' : '?', x, y + 5);
      } else {
        ctx.beginPath();
        ctx.arc(x, y, r + 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.save();
    ctx.translate((px - b.minX) * sx, (pz - b.minZ) * sz);
    ctx.rotate(-facing + Math.PI);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#000';
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6, 7);
    ctx.lineTo(0, 3);
    ctx.lineTo(-6, 7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
}
